import { Button } from "@/components/ui/button";
import { useKeyboardShortcut } from "@/hooks/useKeyboardShortcut";
import { cn } from "@/utils/cn";
import { useRef, useState } from "react";

interface PromoCodeFieldProps {
  /** Placeholder text shown inside the input. */
  placeholder: string;
  /** Label for the apply button (e.g. "Apply"). */
  applyText: string;
  /** Promo code state and handlers from usePromoCodeComponent. */
  promoCode: {
    inputValue: string;
    setInputValue: (value: string) => void;
    handleApply: () => void;
    isApplying?: boolean;
    isInvalid?: boolean;
    isDisabled?: boolean;
  };
}

/**
 * Text input for entering and submitting a promo / coupon code.
 *
 * Handles the "dirty" state internally so the invalid-code error only
 * appears after a failed apply attempt and disappears once the user
 * starts typing again. Supports Enter-key submission.
 */
export function PromoCodeField({
  placeholder,
  applyText,
  promoCode,
}: PromoCodeFieldProps) {
  const {
    inputValue: value,
    setInputValue: onChange,
    handleApply: onApply,
    isApplying = false,
    isInvalid = false,
    isDisabled: disabled = false,
  } = promoCode;

  const inputRef = useRef<HTMLInputElement>(null);
  const [isDirtied, setIsDirtied] = useState(false);

  const showInvalidState = !isApplying && isInvalid && !isDirtied;

  const handleInputChange = (inputValue: string) => {
    if (!isDirtied && inputValue !== value) {
      setIsDirtied(true);
    }
    onChange(inputValue);
  };

  function handleOnApply() {
    setIsDirtied(false);
    onApply();
  }

  useKeyboardShortcut(
    { key: "Enter" },
    (event) => {
      if (
        inputRef.current &&
        document.activeElement === inputRef.current &&
        value.trim() &&
        !isApplying &&
        !disabled
      ) {
        event.preventDefault();
        handleOnApply();
      }
    },
    {
      target: inputRef.current,
      enabled: !disabled && !isApplying,
      priority: 10,
      description: "Apply promo code on Enter",
    },
  );

  return (
    <div id="Promo-Code-Field">
      <div
        className={cn(
          "flex h-10 items-center rounded-lg border border-[#DADADA] bg-white p-1",
          showInvalidState && "border-edge-error",
        )}
      >
        <div className="flex w-full items-center justify-between px-2">
          <input
            ref={inputRef}
            tabIndex={-1}
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={(e) => handleInputChange(e.target.value)}
            disabled={disabled}
            className="flex-1 bg-transparent font-archivo text-xs leading-5 font-normal uppercase outline-hidden disabled:opacity-50 sm:text-sm"
          />
          <Button
            variant="inline-accent"
            onClick={handleOnApply}
            isLoading={isApplying}
            disabled={disabled || !value.trim()}
          >
            {applyText}
          </Button>
        </div>
      </div>

      {showInvalidState && (
        <p className="mt-1 text-xs leading-[20.4px] font-light tracking-[0.12px] text-error-primary">
          Invalid coupon code
        </p>
      )}
    </div>
  );
}
